/**
 * What the "here's what's in the file" step renders. Both platforms show the
 * same numbers because they read them from here.
 */
import { effectiveKind, isAssignedZeroRow } from './buildImportPayload';
import type { SplitwiseFile, SplitwiseWarning } from './splitwiseCsv';

export interface ImportSummary {
  rows: number;
  expenses: number;
  payments: number;
  skipped: number;
  members: string[];
  currencies: Array<{ code: string; rows: number }>;
  dateRange: { from: string; to: string };
  warnings: SplitwiseWarning[];
}

/**
 * `zeroRowAssignments` and `categoryOverrides` are the review's: a
 * no-balance-effect row the reviewer has given a payer imports as an expense,
 * and a flipped label changes its rows' kind, so the counts follow the
 * decisions rather than the parse.
 */
export function summarizeImportFile(
  file: SplitwiseFile,
  zeroRowAssignments: Record<number, number> = {},
  categoryOverrides: Record<string, string> = {}
): ImportSummary {
  const counts = new Map<string, number>();
  const kinds = { expense: 0, payment: 0, skipped: 0 };
  let assigned = 0;
  for (const row of file.rows) {
    counts.set(row.currency, (counts.get(row.currency) ?? 0) + 1);
    kinds[effectiveKind(row, categoryOverrides)] += 1;
    if (isAssignedZeroRow(row, zeroRowAssignments, categoryOverrides))
      assigned += 1;
  }

  return {
    rows: file.rows.length,
    expenses: kinds.expense + assigned,
    payments: kinds.payment,
    skipped: kinds.skipped - assigned,
    members: file.members,
    // `file.currencies` is already most-frequent-first; keep that order.
    currencies: file.currencies.map(code => ({
      code,
      rows: counts.get(code) ?? 0,
    })),
    dateRange: file.dateRange,
    warnings: file.warnings,
  };
}

export interface ImportWarningLine {
  key: string;
  params?: Record<string, unknown>;
}

/**
 * The warning lines the summary renders, in file order. Rows with no balance
 * effect and labels without a match are left out: the skipped stat already
 * counts the former, and the review step is where both are decided; a
 * warning about what the next screen solves only alarms.
 */
export function summaryWarningLines(file: SplitwiseFile): ImportWarningLine[] {
  const lines: ImportWarningLine[] = [];

  for (const warning of file.warnings) {
    if (warning.code === 'unbalanced-row') {
      lines.push({
        key: 'import:summary.unbalanced',
        params: { rowIndex: warning.rowIndex },
      });
    }
  }

  return lines;
}
